import React, {useEffect, useRef, useState} from 'react';
import Landmark from '@core/scripts/components/landmark-adapter';

// make sure all landmarks are only sent once per page view
let startLandmarkSent = false;
let stopLandmarkSent = false;
let progress50LandmarkSent = false;
let progress100LandmarkSent = false;

export default function ProductVideo({video, itemNumber}) {

    const videoRef = useRef(null);
    const duration = useRef(null);
    const visibleRef = useRef(false);

    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsVisible(entry.isIntersecting);
            },
            { threshold: 0.5 }
        );

        if (videoRef.current) {
            observer.observe(videoRef.current);
        }

        return () => {
            if (videoRef.current) {
                observer.unobserve(videoRef.current);
            }
        };
    }, []);

    useEffect(() => {
        visibleRef.current = isVisible; // making the current visibility state accessible for cleanup

        if (videoRef.current) {
            if (isVisible) {
                videoRef.current.play();
            } else {
                videoRef.current.pause();
                videoRef.current.currentTime = 0;
            }
        }
    }, [isVisible]);

    useEffect(() => {
        return () => {
            if (visibleRef.current && !stopLandmarkSent) {
                sendLandmark('stop', video.videoUrl, itemNumber, duration.current);
                stopLandmarkSent = true;
            }
            duration.current = null;
        };
    }, []);

    useEffect(() => {
        // make duration accessible for sending video landmarks on cleanup
        if (videoRef.current && videoRef.current.duration) {
            duration.current = videoRef.current.duration;
        }
    }, [videoRef.current]);

    const onClick = () => {
        if (videoRef.current.paused) {
            videoRef.current.play();
        } else {
            videoRef.current.pause();
        }
    };

    const onPlay = () => {
        if (!startLandmarkSent) {
            sendLandmark('start', video.videoUrl, itemNumber, videoRef.current.duration);
            startLandmarkSent = true;
        }
    };

    const onPause = () => {
        if (!stopLandmarkSent) {
            sendLandmark('stop', video.videoUrl, itemNumber, videoRef.current.duration);
            stopLandmarkSent = true;
        }
    };

    const onTimeUpdate = () => {
        const currentTime = videoRef.current.currentTime;

        if (!progress50LandmarkSent && currentTime >= videoRef.current.duration / 2) {
            sendLandmark('progress_50', video.videoUrl, itemNumber, videoRef.current.duration);
            progress50LandmarkSent = true;
        }

        // time update is only triggered a few times per second, depending on e.g. frame rate and video speed
        // ended event is not fired when video is played in a loop, so we need to check if the video is almost finished
        if (!progress100LandmarkSent && currentTime >= videoRef.current.duration - 0.2) {
            sendLandmark('complete', video.videoUrl, itemNumber, videoRef.current.duration);
            progress100LandmarkSent = true;
        }
    };

    const sendLandmark = (action, videoUrl, itemNumber, duration) => {
        const parts = videoUrl.split('/');
        const fileName =  parts.slice(parts.length - 2).join('_');
        const videoId = fileName.substring(0, fileName.lastIndexOf('.'));

        Landmark.sendLandmark({
            landmark: 'video',
            action: action,
            videoId: videoId,
            itemNumber: itemNumber,
            filename: fileName,
            videoLength: Math.round(duration)
        });
    };

    return (
        <div className="product-image__wrapper -video">
            <img className="product-video__fallback-image"
                 src={video.fallbackImage}
                 srcSet={`${video.fallbackImage} 1x, ${video.fallbackImage2x} 2x`}
                 alt={video.altText}
            />
            <video className="product-video" ref={videoRef} key={video.videoUrl} muted loop playsInline
                   onClick={onClick} onPlay={onPlay} onPause={onPause} onTimeUpdate={onTimeUpdate}
                   title={video.altText}>
                <source src={video.videoUrl} type="video/mp4"/>
            </video>
        </div>
    );
}